import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { paraElServidor } from "@/lib/decimales"

import { type Banco, type ClaseCuenta, type Cuenta, type Divisa, TIPOS_CUENTA } from "./tipos"

/**
 * Abrir una cuenta de la agencia, o ajustar una que ya existe.
 *
 * ⚠ **Lo que se pide depende de la CLASE de cuenta.** No todo lo que guarda dinero es
 * una cuenta de banco: un banco venezolano se identifica con su número de veinte
 * dígitos y su banco del catálogo, uno de fuera con un IBAN y su SWIFT, un exchange
 * con un ID de usuario, una billetera con su dirección y su red, y el efectivo con el
 * sitio donde está. Un formulario único obligaba a dejar media ficha en blanco o,
 * peor, a inventarse un número de cuenta para la caja chica. La lista de campos vive
 * en `CAMPOS`; lo obligatorio lo vuelve a exigir el servidor.
 *
 * ⚠ **La red de una billetera no es decoración.** Mandar USDT por la red equivocada
 * es perderlo, así que se escribe donde se lee al pagar.
 *
 * ⚠ **La identidad de la cuenta se pide aquí y no vuelve a salir.** La lista blanca de
 * `CuentaOut` no devuelve el número, el ID, la dirección ni la cédula —sólo la
 * terminación— y la bitácora los redacta. Al editar, un campo de identidad en blanco
 * significa «déjalo como está», no «bórralo»: no se puede enseñar lo que hay para
 * confirmarlo, y para verlo está «Copiar datos», que pide el PIN y deja rastro.
 *
 * ⚠ **El PIN ya no se pone aquí** (09/10): se genera, cambia y quita en Credenciales →
 * «PIN de bancos», con el cofre abierto y su copia cifrada. El servidor rechaza un
 * `pin` por esta vía (`PIN_EN_CREDENCIALES`); aquí sólo se dice dónde está.
 */

const ESTILO = {
  borderColor: "var(--border)",
  backgroundColor: "var(--surface)",
  color: "var(--fg)",
}

type Campo = {
  nombre: keyof typeof VACIO
  etiqueta: string
  ayuda?: string
  /** Sin esto, el campo es de texto libre. */
  opciones?: "bancos" | "tipos"
}

/** ⚠ La tabla de qué pide cada clase. La misma que `tesoreria.DATOS_POR_CLASE`. */
const CAMPOS: Record<ClaseCuenta, Campo[]> = {
  BANCO_NACIONAL: [
    { nombre: "banco_id", etiqueta: "Banco", opciones: "bancos" },
    { nombre: "numero_cuenta", etiqueta: "Número de cuenta", ayuda: "Los 20 dígitos." },
    { nombre: "tipo", etiqueta: "Tipo", opciones: "tipos" },
    {
      nombre: "cedula",
      etiqueta: "Cédula del titular",
      ayuda: "Un pago móvil la pide, igual que el nombre.",
    },
  ],
  BANCO_INTERNACIONAL: [
    { nombre: "banco_nombre", etiqueta: "Banco" },
    { nombre: "numero_cuenta", etiqueta: "Número o IBAN" },
    { nombre: "swift", etiqueta: "SWIFT / ABA" },
    { nombre: "pais", etiqueta: "País" },
  ],
  EXCHANGE: [
    { nombre: "identificador", etiqueta: "ID o correo", ayuda: "El usuario con el que se recibe." },
    { nombre: "red", etiqueta: "Red", ayuda: "Si aplica: TRC20, ERC20…" },
  ],
  CRIPTO: [
    { nombre: "direccion", etiqueta: "Dirección de la billetera" },
    {
      nombre: "red",
      etiqueta: "Red",
      ayuda: "TRC20, ERC20, BEP20. Equivocarla es perder el envío.",
    },
  ],
  EFECTIVO: [
    { nombre: "ubicacion", etiqueta: "Dónde está", ayuda: "«Caja chica oficina», «Caja fuerte»." },
  ],
}

const CLASES: { valor: ClaseCuenta; etiqueta: string }[] = [
  { valor: "BANCO_NACIONAL", etiqueta: "Banco nacional" },
  { valor: "BANCO_INTERNACIONAL", etiqueta: "Banco internacional" },
  { valor: "EXCHANGE", etiqueta: "Exchange (Binance…)" },
  { valor: "CRIPTO", etiqueta: "Billetera de cripto" },
  { valor: "EFECTIVO", etiqueta: "Efectivo" },
]

const VACIO = {
  nombre: "",
  clase: "BANCO_NACIONAL" as ClaseCuenta,
  codigo_divisa: "BS",
  titular: "",
  cedula: "",
  banco_id: "",
  banco_nombre: "",
  numero_cuenta: "",
  tipo: "CORRIENTE",
  swift: "",
  pais: "",
  identificador: "",
  direccion: "",
  red: "",
  ubicacion: "",
}

export function DialogoCuenta({
  abierto,
  cuenta,
  onCerrar,
  onCreada,
}: {
  abierto: boolean
  /** La que se edita. Sin ella, el diálogo da de alta. */
  cuenta?: Cuenta | null
  onCerrar: () => void
  /** Para dejarla elegida en la lista en cuanto existe. */
  onCreada?: (cuenta: Cuenta) => void
}) {
  const clienteQuery = useQueryClient()
  const editando = cuenta != null
  const [campos, setCampos] = useState({ ...VACIO })
  const [sobregiro, setSobregiro] = useState(false)
  const [fondeo, setFondeo] = useState(false)
  // (23/10, RM-5) El fondo fijo de una caja chica: hasta cuánto se repone.
  const [fondoFijo, setFondoFijo] = useState("")
  // La clave remonta el estado al abrir otra cuenta: un `useEffect` dejaría un render
  // con los datos de la anterior.
  const [ultima, setUltima] = useState<number | null>(null)
  if (abierto && (cuenta?.id ?? null) !== ultima) {
    setUltima(cuenta?.id ?? null)
    // Al abrir otra cuenta, la casilla toma la suya: si no, editar una cuenta normal
    // después de una de sobregiro la marcaría sin que nadie lo pidiera.
    setSobregiro(cuenta?.permite_sobregiro ?? false)
    setFondeo(cuenta?.fondeo_automatico ?? false)
    setFondoFijo(cuenta?.fondo_fijo ?? "")
    setCampos(
      cuenta
        ? {
            ...VACIO,
            nombre: cuenta.nombre,
            clase: cuenta.clase,
            titular: cuenta.titular ?? "",
            banco_id: cuenta.banco_id ? String(cuenta.banco_id) : "",
            banco_nombre: cuenta.banco_nombre ?? "",
            tipo: cuenta.tipo ?? "CORRIENTE",
            swift: cuenta.swift ?? "",
            pais: cuenta.pais ?? "",
            red: cuenta.red ?? "",
            ubicacion: cuenta.ubicacion ?? "",
          }
        : { ...VACIO },
    )
  }

  const bancos = useQuery({
    queryKey: ["bancos", "activos"],
    queryFn: () => api.get<Banco[]>("/api/v1/bancos?solo_activos=true"),
    enabled: abierto,
  })
  const divisas = useQuery({
    queryKey: ["divisas", "activas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas?activo=true"),
    enabled: abierto,
  })

  const guardar = useMutation({
    meta: { exito: "Cuenta guardada" },
    mutationFn: () => {
      const propios = CAMPOS[campos.clase].map((c) => c.nombre)
      const cuerpo: Record<string, unknown> = {
        nombre: campos.nombre.trim(),
        clase: campos.clase,
        titular: campos.titular.trim() || null,
      }
      if (!editando) cuerpo.codigo_divisa = campos.codigo_divisa
      for (const campo of propios) {
        const valor = String(campos[campo] ?? "").trim()
        // Al editar, en blanco es «déjalo como está»: el servidor conserva lo que
        // había. Al crear, el servidor exige lo que falte.
        if (valor === "" && editando) continue
        cuerpo[campo] = campo === "banco_id" ? (valor ? Number(valor) : null) : valor
      }
      cuerpo.permite_sobregiro = sobregiro
      cuerpo.fondeo_automatico = fondeo
      if (editando) cuerpo.fondo_fijo = fondoFijo.trim() ? paraElServidor(fondoFijo) : "0"
      return editando
        ? api.patch<Cuenta>(`/api/v1/cuentas-bancarias/${cuenta.id}`, cuerpo)
        : api.post<Cuenta>("/api/v1/cuentas-bancarias", cuerpo)
    },
    onSuccess: (guardada) => {
      clienteQuery.invalidateQueries({ queryKey: ["cuentas-bancarias"] })
      clienteQuery.invalidateQueries({ queryKey: ["bancos"] })
      setCampos({ ...VACIO })
      setUltima(null)
      onCreada?.(guardada)
      onCerrar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (campos.nombre.trim()) guardar.mutate()
  }

  const escribir = (campo: keyof typeof VACIO, valor: string) =>
    setCampos((previos) => ({ ...previos, [campo]: valor }))

  return (
    <Modal
      abierto={abierto}
      titulo={editando ? `Editar ${cuenta.nombre}` : "Nueva cuenta"}
      descripcion="Cada clase de cuenta pide sus propios datos."
      onCerrar={onCerrar}
    >
      <form onSubmit={enviar} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="c-nombre" className="mb-1 block text-xs font-medium uppercase">
            Nombre
          </label>
          <input
            id="c-nombre"
            value={campos.nombre}
            onChange={(e) => escribir("nombre", e.target.value)}
            placeholder="Operativa Bs"
            maxLength={80}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={ESTILO}
          />
        </div>

        <div>
          <label htmlFor="c-clase" className="mb-1 block text-xs font-medium uppercase">
            Clase
          </label>
          <select
            id="c-clase"
            value={campos.clase}
            onChange={(e) => escribir("clase", e.target.value)}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={ESTILO}
          >
            {CLASES.map((c) => (
              <option key={c.valor} value={c.valor}>
                {c.etiqueta}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="c-divisa" className="mb-1 block text-xs font-medium uppercase">
            Divisa
          </label>
          <select
            id="c-divisa"
            value={campos.codigo_divisa}
            onChange={(e) => escribir("codigo_divisa", e.target.value)}
            disabled={editando}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm disabled:opacity-60"
            style={ESTILO}
          >
            {divisas.data?.map((d) => (
              <option key={d.id} value={d.codigo}>
                {d.codigo} · {d.nombre}
              </option>
            ))}
          </select>
        </div>

        {CAMPOS[campos.clase].map((campo) => (
          <div key={campo.nombre}>
            <label
              htmlFor={`c-${campo.nombre}`}
              className="mb-1 block text-xs font-medium uppercase"
            >
              {campo.etiqueta}
            </label>
            {campo.opciones === "bancos" ? (
              <select
                id={`c-${campo.nombre}`}
                value={campos.banco_id}
                onChange={(e) => escribir("banco_id", e.target.value)}
                className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
                style={ESTILO}
              >
                <option value="">Elige el banco</option>
                {bancos.data?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.codigo ? `${b.codigo} · ` : ""}
                    {b.nombre}
                  </option>
                ))}
              </select>
            ) : campo.opciones === "tipos" ? (
              <select
                id={`c-${campo.nombre}`}
                value={campos.tipo}
                onChange={(e) => escribir("tipo", e.target.value)}
                className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
                style={ESTILO}
              >
                {TIPOS_CUENTA.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`c-${campo.nombre}`}
                value={String(campos[campo.nombre] ?? "")}
                onChange={(e) => escribir(campo.nombre, e.target.value)}
                maxLength={120}
                placeholder={editando ? "Sin cambios" : undefined}
                className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
                style={ESTILO}
              />
            )}
            {campo.ayuda && (
              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                {campo.ayuda}
              </p>
            )}
          </div>
        ))}

        <div>
          <label htmlFor="c-titular" className="mb-1 block text-xs font-medium uppercase">
            Titular o responsable
          </label>
          <input
            id="c-titular"
            value={campos.titular}
            onChange={(e) => escribir("titular", e.target.value)}
            maxLength={120}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={ESTILO}
          />
        </div>

        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {cuenta?.tiene_pin ? "Esta cuenta tiene PIN. " : ""}El PIN de la cuenta se genera, cambia
          o quita en Credenciales → «PIN de bancos».
        </p>

        {/* ⚠ **Por omisión NO, y la excepción se concede a mano.** Un saldo negativo
            casi siempre es un movimiento que falta —una reposición sin registrar—, y
            dejar pasar el egreso esconde el error en vez de enseñarlo. Pero del
            exchange se adelanta el gasto de la oficina y queda debiendo hasta que se
            le repone: ahí negarse obliga a inventar una entrada que no ocurrió. */}
        <label className="flex items-start gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            checked={sobregiro}
            onChange={(e) => setSobregiro(e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="block font-medium">Admite sobregiro</span>
            <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
              Puede quedar en negativo: se usa para adelantar gastos que se reponen después. Sin
              esto, el sistema rechaza el pago que deje la cuenta en rojo.
            </span>
          </span>
        </label>

        {/* ⚠ **Esto NO es sobregiro con otro nombre.** Aquél dice «el negativo es la
            verdad y se deja pasar»; éste dice «el negativo es un espejismo porque el
            dinero entró y no lo apuntamos». Es la cuenta CONDUCTO: la de Zelle, desde
            la que se paga con dinero que llega de fuera en el momento y cuyo
            disponible real no se conoce aquí. */}
        <label className="flex items-start gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            checked={fondeo}
            onChange={(e) => setFondeo(e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="block font-medium">Cada pago trae su fondeo</span>
            <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
              La cuenta no guarda saldo: al ejecutar un pago se registra antes el ingreso que lo
              cubre, y se anula con él si el pago se revierte. Para cuentas que son un conducto, no
              un fondo.
            </span>
          </span>
        </label>

        {editando && campos.clase === "EFECTIVO" && (
          <label className="block text-sm sm:col-span-2">
            Fondo fijo de caja chica (opcional)
            <input
              className="campo mt-1 tabular-nums"
              inputMode="decimal"
              value={fondoFijo}
              placeholder="Ej.: 300"
              onChange={(e) => setFondoFijo(e.target.value)}
            />
            <span className="mt-1 block text-xs" style={{ color: "var(--fg-muted)" }}>
              Hasta cuánto se repone la caja. El Resumen macro dice cuánto falta para reponerla.
              Vacío: no es caja chica.
            </span>
          </label>
        )}

        {guardar.isError && (
          <p role="alert" className="text-sm sm:col-span-2" style={{ color: "var(--destructive)" }}>
            {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
          </p>
        )}

        <div className="flex justify-end gap-2 sm:col-span-2">
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" type="submit" disabled={guardar.isPending}>
            {guardar.isPending ? "Guardando…" : editando ? "Guardar" : "Crear"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
